import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Bell, Search, Home as HomeIcon, BookOpen, Bookmark, Video, Users, Radio, LogOut, User, Settings, MessageCircle, Upload, ChevronDown, BarChart3, Shield, Palette, LayoutDashboard, Gift, Camera, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import axios from "axios";
import { useAuth } from "../App";
import { LOGO_SRC, LOGO_ALT } from "../config/brand";
import AdminNotificationBell from "./AdminNotificationBell";
import Notifications from "./Notifications";
import MessagesInboxPanel from "./MessagesInboxPanel";
import FloatingChatWindow from "./FloatingChatWindow";

const BACKEND_URL = '';  // Use relative URLs

const NewNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [inboxOpen, setInboxOpen] = useState(false);
  const [chatContact, setChatContact] = useState(null);
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const searchInputRef = React.useRef(null);
  const searchContainerRef = React.useRef(null);

  const mainNavItems = [
    { name: "Comics", path: "/comics", icon: <LayoutDashboard className="w-4 h-4" /> },
    { name: "Novels", path: "/novels", icon: <BookOpen className="w-4 h-4" /> },
    { name: "Gallery", path: "/art-gallery", icon: <Palette className="w-4 h-4" /> },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    setIsOpen(false);
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  // Handle click outside search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };

    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSearchOpen]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const isActive = (path) => {
    if (path === "/home" && (location.pathname === "/" || location.pathname === "/home")) {
      return true;
    }
    return location.pathname.startsWith(path) && path !== "/home";
  };

  // Detect admin users FIRST - admin is top-level override
  const isAdmin = !!user && (user.role === 'admin' || user.is_admin === true);

  // Check if user is a creator - simplified role system
  const isCreator = !isAdmin && !!user && (
    user.is_creator === true ||
    user.role === 'creator' ||
    user.role === 'artist' ||
    user.role === 'author'
  );

  // Admins and creators can use messaging
  const canMessage = isAdmin || isCreator;

  // Poll message unread count for users who can message
  const fetchMsgUnread = useCallback(async () => {
    if (!canMessage) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/messages/unread-count", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsgUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [canMessage]);

  useEffect(() => {
    if (!canMessage) return;
    fetchMsgUnread();
    const id = setInterval(fetchMsgUnread, 30000);
    return () => clearInterval(id);
  }, [canMessage, fetchMsgUnread]);

  // Hide navigation entirely on Page Builder Studio routes
  const isPageBuilderStudio = location.pathname.includes('/admin/page-builder/') && 
                              !location.pathname.endsWith('/page-builder');
  
  if (isPageBuilderStudio) {
    return null;
  }

  return (
    <>
    <header className="border-b border-gray-800 bg-black/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Left section - Logo & primary nav */}
          <div className="flex items-center space-x-4">
            <Link 
              to="/home" 
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="Home"
            >
              <img
                src={LOGO_SRC}
                alt={LOGO_ALT}
                className="h-8 md:h-10 w-auto object-contain drop-shadow-md"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center space-x-1">
              {mainNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isActive(item.path)
                      ? "bg-orange-500 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Right section - Icons & user menu */}
          <div className="flex items-center space-x-2">
            {/* Search Icon & Dropdown */}
            <div className="hidden md:flex items-center relative" ref={searchContainerRef}>
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                  isSearchOpen 
                    ? 'bg-orange-500 text-white' 
                    : 'hover:bg-gray-800 text-gray-300'
                }`}
                aria-label="Search"
                aria-expanded={isSearchOpen}
              >
                <Search className="w-4 h-4" />
              </button>
              
              {/* Search Dropdown */}
              {isSearchOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-xl overflow-hidden z-50">
                  <form onSubmit={handleSearchSubmit} className="p-3">
                    <div className="flex items-center bg-gray-800 rounded-lg px-3 py-2 border border-gray-700 focus-within:border-orange-500 transition-colors">
                      <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search creators, comics, novels..."
                        className="flex-1 bg-transparent border-none text-sm text-white placeholder-gray-500 focus:outline-none"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery("")}
                          className="text-gray-500 hover:text-gray-300 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs text-gray-500">Press Enter to search</span>
                      <button
                        type="submit"
                        disabled={!searchQuery.trim()}
                        className="text-xs bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:text-gray-500 text-white px-3 py-1 rounded-full transition-colors"
                      >
                        Search
                      </button>
                    </div>
                  </form>
                  <div className="border-t border-gray-800 px-3 py-2">
                    <p className="text-xs text-gray-500">Quick searches:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {['Action', 'Romance', 'Fantasy', 'Sci-Fi'].map((genre) => (
                        <button
                          key={genre}
                          type="button"
                          onClick={() => {
                            navigate(`/search?q=${encodeURIComponent(genre)}`);
                            setIsSearchOpen(false);
                            setSearchQuery("");
                          }}
                          className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-2 py-1 rounded-full transition-colors"
                        >
                          {genre}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Messages (Chat) - Only for creators and admins */}
            {canMessage && (
              <div className="hidden sm:block relative" data-testid="messages-nav-container">
                <button
                  onClick={() => setInboxOpen((prev) => !prev)}
                  className="relative flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-800 text-gray-300 transition-colors"
                  aria-label="Messages"
                  title="Messages"
                  data-testid="messages-inbox-trigger"
                >
                  <MessageCircle className="w-4 h-4" />
                  {msgUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="messages-unread-badge">
                      {msgUnreadCount > 9 ? '9+' : msgUnreadCount}
                    </span>
                  )}
                </button>
                <MessagesInboxPanel
                  open={inboxOpen}
                  onClose={() => setInboxOpen(false)}
                  onOpenChat={(contact) => { setChatContact(contact); setInboxOpen(false); }}
                />
              </div>
            )}

            {/* Notifications (Bell) */}
            <div className="hidden sm:block">
              <Notifications />
            </div>

            {/* Admin Notification Bell (only for admins) */}
            {isAdmin && (
              <div className="hidden sm:block">
                <AdminNotificationBell />
              </div>
            )}

            {/* Publish button (desktop) */}
            {user && (
              <Button
                onClick={() => navigate("/publish")}
                className="hidden sm:inline-flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                <Upload className="w-3 h-3" />
                <span>Publish</span>
              </Button>
            )}

            {/* Profile dropdown */}
            {user ? (
              (() => {
                const avatarUrl = user.profile_image_url || user.avatar_url || null;
                const displayName = user.display_name || user.username || user.email?.split('@')[0] || '';
                const avatarLetter = (displayName || 'U').trim()[0].toUpperCase();
                const roleLabel = isAdmin ? 'Admin' : isCreator ? 'Creator' : 'Member';
                return (
              <div className="hidden sm:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center justify-center w-9 h-9 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black hover:ring-2 hover:ring-orange-500/60 transition-all"
                      aria-label="Account menu"
                      title="Account"
                      data-testid="user-avatar-btn"
                    >
                      <Avatar className="h-9 w-9">
                        {avatarUrl ? (
                          <AvatarImage
                            src={avatarUrl}
                            alt={displayName}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : null}
                        <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-sm font-bold">
                          {avatarLetter}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={8} className="w-60 bg-zinc-900 border-zinc-700">
                    <DropdownMenuLabel className="font-normal px-3 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative flex-shrink-0 group cursor-pointer" onClick={() => { navigate('/profile/editor/creator'); }} title="Change profile picture">
                          <Avatar className="h-10 w-10">
                            {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
                            <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-sm font-bold">
                              {avatarLetter}
                            </AvatarFallback>
                          </Avatar>
                          <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Camera className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <div className="flex flex-col min-w-0">
                          <p className="text-sm font-semibold text-white truncate" data-testid="dropdown-user-name">{displayName}</p>
                          <p className="text-xs text-zinc-400 truncate" data-testid="dropdown-user-email">{user.email}</p>
                          <span className="text-[10px] text-zinc-500 mt-0.5">{roleLabel}</span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-zinc-700" />

                    {/* A) Admin menu - moderation only */}
                    {isAdmin ? (
                      <>
                        <DropdownMenuItem 
                          onClick={() => navigate("/admin")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <Shield className="w-4 h-4 mr-2 text-orange-400" />
                          <span>Admin Dashboard</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/profile")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <User className="w-4 h-4 mr-2" />
                          <span>View Profile</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/community")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          <span>Community</span>
                        </DropdownMenuItem>
                      </>
                    ) : isCreator ? (
                      <>
                        {/* B) Creator menu */}
                        <DropdownMenuItem 
                          onClick={() => navigate("/profile")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <User className="w-4 h-4 mr-2" />
                          <span>View Profile</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/dashboard")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <BarChart3 className="w-4 h-4 mr-2" />
                          <span>Creator Studio</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/creator/analytics")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                          data-testid="nav-analytics-link"
                        >
                          <TrendingUp className="w-4 h-4 mr-2" />
                          <span>Analytics</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/reader-library")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          <span>My Library</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/saved")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                          data-testid="nav-saved-link"
                        >
                          <Bookmark className="w-4 h-4 mr-2" />
                          <span>Saved</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/community")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          <span>Community</span>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        {/* C) Basic user menu */}
                        <DropdownMenuItem 
                          onClick={() => navigate("/profile")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <User className="w-4 h-4 mr-2" />
                          <span>View Profile</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/dashboard")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          <span>My Library</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/community")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          <span>Community</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate("/publish")}
                          className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          <span>Become a Creator</span>
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
                );
              })()
            ) : (
              <div className="hidden sm:flex items-center space-x-2">
                <Link to="/auth" className="text-xs text-gray-300 hover:text-white">
                  Sign in
                </Link>
              </div>
            )}

            {/* Mobile search icon */}
            <button
              className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-800 text-gray-300 mr-1"
              onClick={() => navigate("/search")}
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Mobile Publish button */}
            {user && (
              <button
                className="md:hidden inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-semibold mr-1"
                onClick={() => navigate("/publish")}
              >
                Publish
              </button>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-800 text-gray-300"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-t border-gray-800 bg-black/95">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-2">
            <form onSubmit={handleSearchSubmit} className="flex items-center bg-gray-900/80 rounded-full px-3 py-1 border border-gray-800 focus-within:border-orange-500 transition-colors">
              <Search className="w-4 h-4 text-gray-400 mr-2" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search creators, comics, or genres"
                className="bg-transparent border-none h-8 px-0 text-sm focus-visible:ring-0 focus-visible:outline-none"
              />
            </form>

            <nav className="grid gap-1">
              {mainNavItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    isActive(item.path)
                      ? "bg-orange-500 text-white"
                      : "text-gray-200 hover:bg-gray-900"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    {item.icon}
                    <span>{item.name}</span>
                  </div>
                  {isActive(item.path) && <span className="text-[10px] uppercase tracking-wide">Current</span>}
                </button>
              ))}

              {canMessage && (
                <button
                  onClick={() => {
                    navigate("/messages");
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                  data-testid="mobile-messages-btn"
                >
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="w-4 h-4" />
                    <span>Messages</span>
                  </div>
                  {msgUnreadCount > 0 && (
                    <span className="bg-blue-500 text-white text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                      {msgUnreadCount > 9 ? '9+' : msgUnreadCount}
                    </span>
                  )}
                </button>
              )}

              <button
                onClick={() => {
                  navigate("/notifications");
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                data-testid="mobile-notifications-btn"
              >
                <div className="flex items-center space-x-2">
                  <Bell className="w-4 h-4" />
                  <span>Notifications</span>
                </div>
              </button>

              <button
                onClick={() => {
                  navigate("/rewards");
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
              >
                <div className="flex items-center space-x-2">
                  <Gift className="w-4 h-4 text-yellow-400" />
                  <span>Rewards</span>
                </div>
              </button>

              {user ? (
                <>
                  {/* A) Admin mobile menu - moderation only */}
                  {isAdmin ? (
                    <>
                      <button
                        onClick={() => {
                          navigate("/admin");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <Shield className="w-4 h-4 text-orange-400" />
                          <span>Admin Dashboard</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4" />
                          <span>View Profile</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/community");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>Community</span>
                        </div>
                      </button>
                    </>
                  ) : isCreator ? (
                    <>
                      {/* B) Creator mobile menu */}
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4" />
                          <span>View Profile</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/dashboard");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <BarChart3 className="w-4 h-4" />
                          <span>Creator Studio</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/creator/analytics");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                        data-testid="mobile-nav-analytics"
                      >
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="w-4 h-4" />
                          <span>Analytics</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/reader-library");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <BookOpen className="w-4 h-4" />
                          <span>My Library</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/saved");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                        data-testid="mobile-nav-saved"
                      >
                        <div className="flex items-center space-x-2">
                          <Bookmark className="w-4 h-4" />
                          <span>Saved</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/community");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>Community</span>
                        </div>
                      </button>
                    </>
                  ) : (
                    <>
                      {/* C) Basic user mobile menu */}
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4" />
                          <span>View Profile</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/dashboard");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <BookOpen className="w-4 h-4" />
                          <span>My Library</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/community");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>Community</span>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          navigate("/publish");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                      >
                        <div className="flex items-center space-x-2">
                          <Upload className="w-4 h-4" />
                          <span>Become a Creator</span>
                        </div>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10"
                  >
                    <div className="flex items-center space-x-2">
                      <LogOut className="w-4 h-4" />
                      <span>Sign out</span>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      navigate("/auth");
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-gray-900"
                  >
                    <span>Sign in</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>

      {/* Floating Chat Window - rendered outside header for proper z-index */}
      {chatContact && canMessage && (
        <FloatingChatWindow
          contact={chatContact}
          onClose={() => setChatContact(null)}
        />
      )}
    </>
  );
};

export default NewNavigation;
